import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PageHeader } from "@/components/PageHeader";
import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ApiError, api, type VaultSecret } from "@/lib/api";
import { relativeTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  type VaultTreeNode,
  ancestorGroups,
  buildVaultTree,
  defaultExpanded,
  filterVaultKeys,
  vaultLeafLabel,
} from "@/lib/vault-tree";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Lock,
  Plus,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { containerPermissions } from "@/lib/boo-permissions";
import {
  type ConsoleTab,
  type StorageProtectionTone,
  openStorageConsole,
  storageProtectionView,
} from "@/lib/storage-protection";

// The brain agent stores every default-vault secret under the "kv" mount
// (see boo-brain slack-http-routes.ts / at-rest-cipher.ts). The backend proxy
// offers no mount-enumeration endpoint, so the dashboard targets this one
// mount — the same one the agent reads from, making secrets written here
// usable by the agent as vault://default/kv/<path>/<field>.
const VAULT_MOUNT = "kv";

// Mirror the brain's validation for fast client-side feedback. The brain is
// the source of truth and rejects with 400 invalid_path / invalid_payload.
const PATH_SEGMENT_RE = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,127}$/;
const FIELD_NAME_RE = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,255}$/;

function isValidPath(path: string): boolean {
  const segments = path.split("/");
  return segments.length > 0 && segments.every((s) => PATH_SEGMENT_RE.test(s));
}

// The proxy forwards the brain's status verbatim. A 404 (vault provider
// disabled) or a 409 that isn't the "not running" race both mean the agent has
// no usable vault yet — show a friendly empty state. Anything else falls
// through to the normal error view, which surfaces the backend's own message.
function isVaultDisabled(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.status === 404) return true;
  return error.status === 409 && !`${error.code} ${error.message}`.toLowerCase().includes("not running");
}

export function VaultTab() {
  const params = useParams({ strict: false }) as { containerId?: string };
  const containerId = params.containerId!;
  const queryClient = useQueryClient();

  const { data: listResponse } = useQuery({
    queryKey: ["containers"],
    queryFn: api.containers.list,
  });
  const container = listResponse?.data.find((c) => c.id === containerId);
  const permissions = containerPermissions(container, listResponse?._permissions);
  // canViewVault is the read-only floor (list keys). canManageVault gates
  // reading plaintext values, writing, and deleting — a member with only
  // canViewVault gets a read-only key listing.
  const canManage = permissions?.canManageVault === true;
  const canView = permissions?.canViewVault === true || canManage;

  const isRunning = container?.deploymentStatus === "running";

  const [search, setSearch] = useState("");
  const [editingPath, setEditingPath] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ["vault", containerId, VAULT_MOUNT],
    queryFn: () => api.containers.vault.list(containerId, VAULT_MOUNT),
    enabled: !!container && isRunning && canView,
    retry: false,
  });

  const removeMutation = useMutation({
    mutationFn: (path: string) => api.containers.vault.remove(containerId, VAULT_MOUNT, path),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["vault", containerId, VAULT_MOUNT] }),
  });

  const allKeys = useMemo(() => listQuery.data?.keys ?? [], [listQuery.data?.keys]);
  const keys = useMemo(() => filterVaultKeys(allKeys, search), [allKeys, search]);
  const tree = useMemo(() => buildVaultTree(keys), [keys]);

  // Group paths that are open. `null` means "untouched": small vaults start
  // fully open, large ones collapsed at the root, re-derived whenever the
  // key list changes. The first toggle or search snapshots that default.
  const [expandedState, setExpandedState] = useState<Set<string> | null>(null);
  const expanded = useMemo(
    () => expandedState ?? defaultExpanded(buildVaultTree(allKeys)),
    [expandedState, allKeys],
  );
  const toggleGroup = (path: string) =>
    setExpandedState((prev) => {
      const next = new Set(prev ?? expanded);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  const updateSearch = (value: string) => {
    setSearch(value);
    // Open every group on the way to a match so the matches are visible;
    // groups the user later collapses stay collapsed.
    if (!value.trim()) return;
    const open = ancestorGroups(filterVaultKeys(allKeys, value));
    if (open.size > 0) setExpandedState((prev) => new Set([...(prev ?? expanded), ...open]));
  };

  if (!container) return null;

  if (container.agentType !== "boo-brain" || !canView) {
    // Visibility is also gated at the tab strip; this guards deep links.
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground p-6">
        The vault isn&apos;t available for this Boo.
      </div>
    );
  }

  if (!isRunning) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground p-6">
        {container?.deploymentStatus === "restarting"
          ? "This Boo is restarting. Its vault is back in a moment."
          : "This Boo is offline. Start it to manage its vault."}
      </div>
    );
  }

  const vaultDisabled = isVaultDisabled(listQuery.error);

  return (
    <div className="flex flex-1 flex-col min-h-0 overflow-auto p-6 gap-4">
      <div className="flex items-end justify-between gap-3">
        <PageHeader
          title="Vault"
          subtitle={
            <>
              Passwords, keys and tokens Boo uses for your apps and connections.
              {!canManage && " You can browse keys but not reveal or change values."}
            </>
          }
        />
        {canManage && !vaultDisabled && (
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="size-3.5" />
            Add secret
          </Button>
        )}
      </div>

      <Collapsible>
        <CollapsibleTrigger className="group inline-flex items-center gap-1.5 rounded-sm text-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <ChevronDown className="size-4 transition-transform group-data-[state=closed]:-rotate-90" />
          For developers: how Boo refers to these
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2 pl-5.5">
          <p className="text-xs text-muted-foreground">
            Boo reads each secret as{" "}
            <code className="font-mono">vault://default/{VAULT_MOUNT}/&lt;path&gt;/&lt;field&gt;</code>.
          </p>
        </CollapsibleContent>
      </Collapsible>

      {canManage && <StorageProtectionCard containerId={containerId} />}

      {vaultDisabled ? (
        <EmptyPanel
          icon={Lock}
          title="Vault isn't enabled for this Boo yet"
          body="This Boo doesn't have a default vault configured. Once it's enabled, secrets will appear here."
        />
      ) : listQuery.isLoading ? (
        <LoadingIndicator />
      ) : listQuery.isError ? (
        <InlineError error={listQuery.error} />
      ) : keys.length === 0 && !search ? (
        <EmptyPanel
          icon={KeyRound}
          title="No secrets yet"
          body={
            canManage
              ? "Add a secret here so the Boo can use it at runtime."
              : "This Boo's vault is empty."
          }
        />
      ) : (
        <>
          <Input
            value={search}
            onChange={(e) => updateSearch(e.target.value)}
            placeholder="Search"
            className="max-w-xs"
          />
          {keys.length === 0 ? (
            <p className="text-sm text-muted-foreground px-1">
              No secrets match &ldquo;{search.trim()}&rdquo;.
            </p>
          ) : (
            <VaultTree
              root={tree}
              expanded={expanded}
              onToggle={toggleGroup}
              onOpen={canManage ? setEditingPath : undefined}
            />
          )}
        </>
      )}

      {/* Edit / create live in the same sheet; `editingPath` chooses mode. */}
      {canManage && (creating || editingPath !== null) && (
        <VaultSecretSheet
          containerId={containerId}
          existingPath={editingPath}
          onClose={() => {
            setCreating(false);
            setEditingPath(null);
          }}
          onDelete={(path) => {
            setEditingPath(null);
            setPendingDelete(path);
          }}
        />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title={`Delete "${pendingDelete ?? ""}"?`}
        description="This permanently removes the secret from the vault. The Boo will no longer be able to resolve references to it."
        confirmLabel="Delete"
        loading={removeMutation.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          removeMutation.mutate(pendingDelete, {
            onSettled: () => setPendingDelete(null),
          });
        }}
      />
    </div>
  );
}

const TONE: Record<StorageProtectionTone, string> = {
  green: "bg-emerald-50 text-emerald-800 ring-emerald-600/15 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-400/20",
  amber: "bg-amber-50 text-amber-900 ring-amber-600/20 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-400/20",
  blue: "bg-sky-50 text-sky-800 ring-sky-600/15 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-400/20",
};

// Shown only for a password-protected brain; renders nothing while the state
// loads, when it can't be read, and for every other kind of protection.
function StorageProtectionCard({ containerId }: { containerId: string }) {
  const stateQuery = useQuery({
    queryKey: ["atrest-state", containerId],
    queryFn: () => api.containers.atrestState(containerId),
    retry: false,
    // Pick up a lock, unlock or new password made in the console tab, even
    // within the global 30s staleTime.
    refetchOnWindowFocus: "always",
  });
  const openMutation = useMutation({
    mutationFn: (tab: ConsoleTab | null) =>
      openStorageConsole(tab, () => api.containers.operatorCertificate(containerId)),
  });

  const view = storageProtectionView(stateQuery.data);
  if (!view) return null;

  // Open the tab inside the click so pop-up blockers allow it; the console
  // URL arrives once the certificate is minted.
  const open = () => openMutation.mutate(window.open("", "_blank"));

  return (
    <section aria-labelledby="storage-protection-title" className="rounded-lg border p-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className="rounded-full bg-muted/50 p-2">
            <ShieldCheck className="size-4 text-muted-foreground" aria-hidden />
          </div>
          <div className="min-w-0 flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 id="storage-protection-title" className="text-sm font-medium">
                Storage protection
              </h3>
              <span
                className={cn(
                  "inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
                  TONE[view.tone],
                )}
              >
                <span className="size-1.5 rounded-full bg-current" aria-hidden />
                {view.label}
              </span>
            </div>
            <p className="text-xs text-muted-foreground max-w-xl">{view.note}</p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={open} loading={openMutation.isPending}>
          {!openMutation.isPending && <ExternalLink className="size-3.5" />}
          Open storage console
        </Button>
      </div>
      {openMutation.isError && <InlineError error={openMutation.error} />}
    </section>
  );
}

// Indent per depth, in px; deeper levels share the cap so very long paths
// don't push labels off the row.
const INDENT_PX = 16;
const MAX_INDENT_DEPTH = 6;
const ROW_PAD_PX = 8;

function rowIndent(depth: number): number {
  return ROW_PAD_PX + Math.min(depth, MAX_INDENT_DEPTH) * INDENT_PX;
}

function VaultTree({
  root,
  expanded,
  onToggle,
  onOpen,
}: {
  root: VaultTreeNode;
  expanded: Set<string>;
  onToggle: (path: string) => void;
  onOpen?: (path: string) => void;
}) {
  return (
    <div className="rounded-md border p-1 flex flex-col">
      <VaultTreeRows node={root} depth={0} expanded={expanded} onToggle={onToggle} onOpen={onOpen} />
    </div>
  );
}

function VaultTreeRows({
  node,
  depth,
  expanded,
  onToggle,
  onOpen,
}: {
  node: VaultTreeNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (path: string) => void;
  onOpen?: (path: string) => void;
}) {
  const indent = { paddingLeft: rowIndent(depth) };
  return (
    <>
      {node.groups.map((group) => {
        const open = expanded.has(group.path);
        return (
          <div key={`g:${group.path}`} className="flex flex-col">
            <button
              type="button"
              onClick={() => onToggle(group.path)}
              aria-expanded={open}
              style={indent}
              title={group.path}
              className="flex min-h-9 w-full items-center gap-1.5 rounded-sm pr-2 py-1.5 text-left text-sm hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronRight
                className={cn(
                  "size-3.5 shrink-0 text-muted-foreground motion-safe:transition-transform",
                  open && "rotate-90",
                )}
                aria-hidden
              />
              <span className="min-w-0 truncate font-medium">{group.name}</span>
              <span className="ml-auto shrink-0 text-xs tabular-nums text-muted-foreground">
                {group.leafCount}
              </span>
            </button>
            {open && (
              <div className="flex flex-col">
                <VaultTreeRows
                  node={group}
                  depth={depth + 1}
                  expanded={expanded}
                  onToggle={onToggle}
                  onOpen={onOpen}
                />
              </div>
            )}
          </div>
        );
      })}
      {node.leaves.map((path) => (
        <VaultLeafRow key={`l:${path}`} path={path} indent={indent} onOpen={onOpen} />
      ))}
    </>
  );
}

function VaultLeafRow({
  path,
  indent,
  onOpen,
}: {
  path: string;
  indent: { paddingLeft: number };
  onOpen?: (path: string) => void;
}) {
  const label = vaultLeafLabel(path);
  const className = cn(
    "flex min-h-9 w-full items-center gap-1.5 rounded-sm pr-2 py-1.5 text-left",
    onOpen && "hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
  );
  const body = (
    <>
      <KeyRound className="size-3.5 shrink-0 text-muted-foreground/70" aria-hidden />
      <span className="min-w-0 truncate font-mono text-xs">{label}</span>
    </>
  );
  if (!onOpen) {
    return (
      <div style={indent} title={path} className={className}>
        {body}
      </div>
    );
  }
  return (
    <button type="button" style={indent} title={path} className={className} onClick={() => onOpen(path)}>
      {body}
    </button>
  );
}

function EmptyPanel({
  icon: Icon,
  title,
  body,
}: {
  icon: typeof KeyRound;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-lg border p-6 flex flex-col items-center text-center gap-3">
      <div className="rounded-full bg-muted/50 p-3">
        <Icon className="size-5 text-muted-foreground" />
      </div>
      <div className="max-w-sm">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{body}</p>
      </div>
    </div>
  );
}

interface FieldRow {
  id: number;
  name: string;
  value: string;
  reveal: boolean;
}

let fieldIdSeq = 0;
function newField(name = "", value = ""): FieldRow {
  return { id: ++fieldIdSeq, name, value, reveal: false };
}

function VaultSecretSheet({
  containerId,
  existingPath,
  onClose,
  onDelete,
}: {
  containerId: string;
  existingPath: string | null;
  onClose: () => void;
  onDelete: (path: string) => void;
}) {
  const queryClient = useQueryClient();
  const isEdit = existingPath !== null;

  const [path, setPath] = useState(existingPath ?? "");
  const [fields, setFields] = useState<FieldRow[]>(isEdit ? [] : [newField()]);
  const [formError, setFormError] = useState<string | null>(null);

  const readQuery = useQuery({
    queryKey: ["vault", containerId, VAULT_MOUNT, existingPath],
    queryFn: () => api.containers.vault.read(containerId, VAULT_MOUNT, existingPath!),
    enabled: isEdit,
    retry: false,
  });

  // Seed the field editor once the secret's current fields load. Loading the
  // full set first is essential: write replaces the whole data object, so we
  // must submit the merged set or editing one field would drop the others.
  // Seed exactly once per open (render-time, React's "you might not need an
  // effect" pattern) so a background refetch can't clobber in-progress edits.
  const [hasSeeded, setHasSeeded] = useState(false);
  if (!hasSeeded && readQuery.data) {
    setHasSeeded(true);
    const rows = Object.entries(readQuery.data.data).map(([name, value]) =>
      newField(name, value),
    );
    setFields(rows.length > 0 ? rows : [newField()]);
  }

  const writeMutation = useMutation({
    mutationFn: (input: { path: string; data: Record<string, string> }) =>
      api.containers.vault.write(containerId, VAULT_MOUNT, input.path, input.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vault", containerId, VAULT_MOUNT] });
      onClose();
    },
  });

  const addField = () => setFields((f) => [...f, newField()]);
  const removeField = (id: number) =>
    setFields((f) => (f.length > 1 ? f.filter((r) => r.id !== id) : f));
  const updateField = (id: number, patch: Partial<FieldRow>) =>
    setFields((f) => f.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const submit = () => {
    setFormError(null);
    const trimmedPath = path.trim();
    if (!trimmedPath || !isValidPath(trimmedPath)) {
      setFormError("Enter a valid path (letters, numbers, _.- per segment, separated by /).");
      return;
    }
    const data: Record<string, string> = {};
    for (const f of fields) {
      const name = f.name.trim();
      if (!name) continue;
      if (!FIELD_NAME_RE.test(name)) {
        setFormError(`Invalid field name "${name}".`);
        return;
      }
      if (name in data) {
        setFormError(`Duplicate field name "${name}".`);
        return;
      }
      data[name] = f.value;
    }
    if (Object.keys(data).length === 0) {
      setFormError("Add at least one field.");
      return;
    }
    writeMutation.mutate({ path: trimmedPath, data });
  };

  const title = isEdit ? "Edit secret" : "Add secret";

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="sm:max-w-lg p-6 overflow-y-auto flex flex-col">
        <SheetHeader className="p-0">
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>
            Values are stored in the Boo&apos;s default vault. They&apos;re masked
            here — reveal one only when you need it.
          </SheetDescription>
        </SheetHeader>

        {isEdit && readQuery.isLoading ? (
          <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading secret…
          </div>
        ) : isEdit && readQuery.isError ? (
          <div className="mt-6">
            <InlineError error={readQuery.error} />
          </div>
        ) : (
          <div className="mt-6 flex flex-col gap-5">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="vault-path">Path</Label>
              <Input
                id="vault-path"
                value={path}
                autoComplete="off"
                readOnly={isEdit}
                onChange={(e) => setPath(e.target.value)}
                placeholder="team/api"
                className={isEdit ? "bg-muted text-muted-foreground font-mono" : "font-mono"}
              />
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <Label>Fields</Label>
                <Button type="button" size="xs" variant="outline" onClick={addField}>
                  <Plus className="size-3" />
                  Add field
                </Button>
              </div>
              {fields.map((f) => (
                <FieldEditor
                  key={f.id}
                  field={f}
                  mount={VAULT_MOUNT}
                  path={path.trim()}
                  canRemove={fields.length > 1}
                  onChange={(patch) => updateField(f.id, patch)}
                  onRemove={() => removeField(f.id)}
                />
              ))}
            </div>

            {formError && <InlineError error={formError} />}
            {writeMutation.isError && <InlineError error={writeMutation.error} />}

            <div className="flex items-center gap-2 pt-1">
              <Button onClick={submit} loading={writeMutation.isPending} className="flex-1">
                Save
              </Button>
              {isEdit && (
                <Button
                  variant="destructive"
                  disabled={writeMutation.isPending}
                  onClick={() => onDelete(existingPath!)}
                >
                  <Trash2 className="size-3.5" />
                  Delete
                </Button>
              )}
            </div>

            {isEdit && readQuery.data && (
              <MetadataFooter metadata={readQuery.data.metadata} />
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function FieldEditor({
  field,
  mount,
  path,
  canRemove,
  onChange,
  onRemove,
}: {
  field: FieldRow;
  mount: string;
  path: string;
  canRemove: boolean;
  onChange: (patch: Partial<FieldRow>) => void;
  onRemove: () => void;
}) {
  const reference =
    path && field.name.trim()
      ? `vault://default/${mount}/${path}/${field.name.trim()}`
      : null;

  return (
    <div className="rounded-md border p-3 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Input
          value={field.name}
          autoComplete="off"
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="field"
          className="font-mono text-sm"
        />
        <Input
          value={field.value}
          type={field.reveal ? "text" : "password"}
          autoComplete="off"
          onChange={(e) => onChange({ value: e.target.value })}
          placeholder="value"
          className="font-mono text-sm"
        />
        <Button
          type="button"
          size="icon"
          variant="ghost"
          onClick={() => onChange({ reveal: !field.reveal })}
          title={field.reveal ? "Hide value" : "Reveal value"}
        >
          {field.reveal ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          disabled={!canRemove}
          onClick={onRemove}
          title="Remove field"
        >
          <Trash2 className="size-4" />
        </Button>
      </div>
      {reference && <ReferenceHint reference={reference} />}
    </div>
  );
}

function ReferenceHint({ reference }: { reference: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(reference);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable (insecure context / denied) — text stays selectable.
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="group flex items-center gap-1.5 text-left text-[11px] text-muted-foreground hover:text-foreground"
      title="Copy reference"
    >
      {copied ? <Check className="size-3 shrink-0" /> : <Copy className="size-3 shrink-0" />}
      <code className="font-mono break-all">{reference}</code>
    </button>
  );
}

function ago(ts: number): string {
  const r = relativeTime(ts);
  return r === "just now" ? r : `${r} ago`;
}

function MetadataFooter({ metadata }: { metadata: VaultSecret["metadata"] }) {
  return (
    <div className="border-t pt-3 text-[11px] text-muted-foreground flex flex-col gap-0.5">
      <span>Created {ago(metadata.createdAtMillis)}</span>
      <span>Updated {ago(metadata.updatedAtMillis)}</span>
    </div>
  );
}
